// Contextual sub-nav (Variant C — Slack/Linear pattern).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { I } from "../shared/Icon";
import { SUBNAVS } from "./subnavs";
import { moduleByPath } from "./modules";
import { api } from "../shared/api/client";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import type { BoardCategory } from "../pages/extras/BoardPage";
import { ProjectsSidebar } from "./ProjectsSidebar";
import { ProgramsSidebar } from "./ProgramsSidebar";
import { ReferencesSidebar } from "./ReferencesSidebar";
import { DriveSidebar } from "./DriveSidebar";
import { MailSidebar } from "./MailSidebar";
import { ApprovalSidebar } from "./ApprovalSidebar";
import { TodosSidebar } from "./TodosSidebar";
import { NoticeSidebar } from "./NoticeSidebar";
import { RoomIconPicker } from "../shared/RoomIconPicker";

type Props = { width: number; onResize: (w: number) => void; collapsed: boolean; onCollapse: (c: boolean) => void };

// ─── Context menu (right-click) — exported for reuse ───────────────────────

// label/onClick은 sep(구분선) 항목에선 생략 — 렌더에서 옵셔널 호출로 처리.
export type CtxItem = { label?: string; icon?: React.ReactNode; onClick?: () => void; danger?: boolean; sep?: boolean };

export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: CtxItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const W = 220, H = items.length * 32 + 16;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);

  return (
    <div
      ref={ref}
      style={{
        position: "fixed", left, top, width: W, zIndex: 1000,
        background: "var(--bg-0, #fff)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        padding: 6, fontSize: 13,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((it, i) => it.sep ? (
        <div key={`sep-${i}`} style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
      ) : (
        <button
          key={i}
          onClick={() => { it.onClick?.(); onClose(); }}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            width: "100%", padding: "6px 10px", border: "none",
            background: "transparent", cursor: "pointer",
            color: it.danger ? "var(--danger, #ef4444)" : "var(--text-1, #1f2937)",
            borderRadius: 4, textAlign: "left", fontSize: 13,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hover, #f3f4f6)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {it.icon && <span style={{ width: 14, display: "inline-flex", justifyContent: "center" }}>{it.icon}</span>}
          <span>{it.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Board category management helpers ─────────────────────────────────────

function useBoardCategories(active: boolean) {
  const [cats, setCats] = useState<BoardCategory[]>([]);

  const reload = () => {
    if (!active) return;
    api.get<BoardCategory[]>("/forum/categories")
      .then(r => setCats(r.data))
      .catch(() => {});
  };

  useEffect(() => { reload(); }, [active]);

  return { cats, setCats, reload };
}

// ─── Notice category management helpers ─────────────────────────────────────

export type NoticeCategory = { id: number; code: string; label: string; color: string; sort_order: number; is_active: boolean };

function useNoticeCategories(active: boolean) {
  const [cats, setCats] = useState<NoticeCategory[]>([]);

  const reload = () => {
    if (!active) return;
    api.get<NoticeCategory[]>("/notice/categories")
      .then(r => setCats(r.data))
      .catch(() => {});
  };

  useEffect(() => { reload(); }, [active]);

  return { cats, setCats, reload };
}

// ─── Reference category management helpers ───────────────────────────────────

export type RefCategory = { id: number; label: string; order: number };

function useReferenceCategories(active: boolean) {
  const [cats, setCats] = useState<RefCategory[]>([]);

  const reload = () => {
    if (!active) return;
    api.get<RefCategory[]>("/references/categories")
      .then(r => setCats(r.data))
      .catch(() => {});
  };

  useEffect(() => { reload(); }, [active]);

  return { cats, setCats, reload };
}

// ─── Color map for category dots ────────────────────────────────────────────

const CAT_DOT_COLOR: Record<string, string> = {
  info: "#3b82f6", danger: "#ef4444", warning: "#f59e0b",
  success: "#22c55e", default: "#94a3b8",
};

// ─── Category item row (with hover management buttons) ──────────────────────

function CatItem({
  cat, active, isAdmin,
  onSelect, onEdit, onDelete, onMove,
  isFirst, isLast,
}: {
  cat: { id: string; label: string; color?: string };
  active: boolean; isAdmin: boolean;
  onSelect: () => void;
  onEdit: (newLabel: string) => void;
  onDelete: () => void;
  onMove: (dir: "up" | "down") => void;
  isFirst: boolean; isLast: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(cat.label);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const beginEdit = () => { setEditLabel(cat.label); setEditing(true); setTimeout(() => inputRef.current?.focus(), 0); };

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditLabel(cat.label);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    const trimmed = editLabel.trim();
    if (trimmed && trimmed !== cat.label) onEdit(trimmed);
    setEditing(false);
  };

  const dotColor = cat.id === ""
    ? "var(--text-3)"
    : (CAT_DOT_COLOR[cat.color ?? ""] ?? "#94a3b8");

  if (editing) {
    return (
      <div style={{ display: "flex", gap: 4, margin: "1px 6px", padding: "4px 6px", alignItems: "center",
        background: "var(--surface)", borderRadius: 6, border: "1px solid var(--p-400, #818cf8)" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={editLabel}
          onChange={e => setEditLabel(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={commitEdit}
          style={{
            flex: 1, fontSize: 13, padding: "1px 4px",
            border: "none", outline: "none",
            background: "transparent", color: "var(--text)",
          }}
        />
        <button title="저장" onClick={commitEdit}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "0 3px",
            color: "var(--p-500, #6366f1)", fontWeight: 700 }}
        >✓</button>
        <button title="취소" onClick={() => setEditing(false)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "0 3px", color: "var(--text-3)" }}
        >✕</button>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onContextMenu={e => { if (isAdmin && cat.id !== "") { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); } }}
      style={{
        display: "flex", alignItems: "center",
        margin: "1px 6px", borderRadius: 6,
        background: active
          ? "rgba(99,102,241,0.12)"
          : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        transition: "background 0.12s",
      }}
    >
      <button
        onClick={onSelect}
        style={{
          flex: 1, display: "flex", alignItems: "center", gap: 8,
          background: "none", border: "none", cursor: "pointer",
          padding: "6px 8px 6px 10px", textAlign: "left",
        }}
      >
        {/* Colored dot indicator */}
        <span style={{
          width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
          background: active ? "var(--p-500, #6366f1)" : dotColor,
          boxShadow: active ? "0 0 0 2px rgba(99,102,241,0.25)" : "none",
          transition: "background 0.15s, box-shadow 0.15s",
        }} />
        <span style={{
          fontSize: 13,
          color: active ? "var(--p-600, #4f46e5)" : "var(--text-2, #374151)",
          fontWeight: active ? 600 : 400,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
        }}>
          {cat.label}
        </span>
      </button>

      {/* Management buttons — show on hover for named categories (not "전체") */}
      {isAdmin && hovered && cat.id !== "" && (
        <div
          style={{ display: "flex", gap: 1, paddingRight: 5, flexShrink: 0 }}
          onMouseDown={e => e.stopPropagation()}
        >
          {!isFirst && (
            <button title="위로" onClick={e => { e.stopPropagation(); onMove("up"); }}
              style={catActionBtn}>↑</button>
          )}
          {!isLast && (
            <button title="아래로" onClick={e => { e.stopPropagation(); onMove("down"); }}
              style={catActionBtn}>↓</button>
          )}
          <button title="이름 변경" onClick={startEdit} style={catActionBtn}>✏</button>
          <button title="삭제" onClick={e => { e.stopPropagation(); onDelete(); }}
            style={{ ...catActionBtn, color: "var(--danger, #ef4444)" }}>✕</button>
        </div>
      )}

      {ctx && (
        <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} items={[
          { label: "이름 변경", icon: "✏️", onClick: beginEdit },
          ...(!isFirst ? [{ label: "위로 이동", icon: "↑", onClick: () => onMove("up") } as CtxItem] : []),
          ...(!isLast ? [{ label: "아래로 이동", icon: "↓", onClick: () => onMove("down") } as CtxItem] : []),
          { sep: true } as CtxItem,
          { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
        ]} />
      )}
    </div>
  );
}

const catActionBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 22, borderRadius: 4, fontSize: 11,
  color: "var(--text-3)", padding: 0,
  transition: "color 0.1s, background 0.1s",
};

// ─── Main SubNav ─────────────────────────────────────────────────────────────

export function SubNav({ width, onResize, collapsed, onCollapse }: Props) {
  const loc = useLocation();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const dragging = useRef<{ startX: number; startW: number } | null>(null);

  const mod = moduleByPath(loc.pathname);
  const isBoard    = mod?.id === "board";
  const isProj     = mod?.id === "proj";
  const isChat     = mod?.id === "msg";
  const isNotice   = mod?.id === "notice";
  const isRef      = mod?.id === "ref";
  const isDrive    = mod?.id === "drive";
  const isExecDocs = mod?.id === "exec_docs";
  const isPrograms = mod?.id === "programs";
  const isMail     = mod?.id === "mail";
  const isApproval = mod?.id === "approval";
  const isTodos    = mod?.id === "todos";

  // Board category management state
  const { cats, setCats, reload: reloadCats } = useBoardCategories(isBoard);

  // Notice category management state
  const { cats: noticeCats, setCats: setNoticeCats, reload: reloadNoticeCats } = useNoticeCategories(isNotice);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const newCatInputRef = useRef<HTMLInputElement>(null);

  // Notice category add state
  const [addingNoticeCat, setAddingNoticeCat] = useState(false);
  const [newNoticeCatLabel, setNewNoticeCatLabel] = useState("");
  const newNoticeCatInputRef = useRef<HTMLInputElement>(null);

  // Reference category management state
  const { cats: refCats, setCats: setRefCats, reload: reloadRefCats } = useReferenceCategories(isRef);
  const [addingRefCat, setAddingRefCat] = useState(false);
  const [newRefCatLabel, setNewRefCatLabel] = useState("");
  const newRefCatInputRef = useRef<HTMLInputElement>(null);

  const authManifest = useAuth(s => s.manifest);
  // Category management (메뉴 수정/추가/삭제) gated by menu_manage, per the
  // configured "메뉴 권한 관리" rules. SUPER_ADMIN always allowed as a fallback.
  // notice/board → 실장(DIVISION_HEAD)+ ; references → 팀장(TEAM_LEAD)+.
  const canManageNoticeBoard = useAuth(s => s.canManageMenu("notice") || s.hasRole("SUPER_ADMIN"));
  const canManageRef = useAuth(s => s.canManageMenu("references") || s.hasRole("SUPER_ADMIN"));
  const isAdmin = canManageNoticeBoard;

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragging.current.startX;
      const next = Math.min(420, Math.max(160, dragging.current.startW + dx));
      onResize(next);
    };
    const up = () => { dragging.current = null; document.body.style.cursor = ""; };
    window.addEventListener("mousemove", handle);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", handle); window.removeEventListener("mouseup", up); };
  }, [onResize]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = { startX: e.clientX, startW: width };
    document.body.style.cursor = "col-resize";
  };

  const subnav = mod ? SUBNAVS[mod.id] : null;
  if (!isProj && !isPrograms && !isExecDocs && !isMail && !isTodos && !isApproval && !subnav) return null;

  if (collapsed) {
    return (
      <div className="t-subnav t-subnav-collapsed" onClick={() => onCollapse(false)}>
        <div className="t-subnav-expand-strip" title="펼치기"><I.ChevR size={12}/></div>
      </div>
    );
  }

  // ── Projects: render the project-list sidebar instead of phase groups ────
  if (isProj) {
    return (
      <div className="t-subnav" style={{ width }}>
        <div className="t-subnav-header">
          <div>
            <h3>프로젝트</h3>
            <p>참여 중인 프로젝트</p>
          </div>
          <button className="t-subnav-collapse" onClick={() => onCollapse(true)} title="접기">
            <I.PanelL size={14}/>
          </button>
        </div>
        <ProjectsSidebar />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Programs: folder-tree sidebar ───────────────────────────────────────
  if (isPrograms) {
    return (
      <div className="t-subnav" style={{ width }}>
        <ProgramsSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── References: folder-tree sidebar (category → tabs) ────────────────────
  if (isRef) {
    return (
      <div className="t-subnav" style={{ width }}>
        <ReferencesSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Drive: folder-tree sidebar (scope → folders → sub-folders) ─────────────
  // key 필수 — /drive ↔ /exec/docs 전환 시 같은 위치의 같은 컴포넌트라 React가
  // 인스턴스를 재사용해 이전 라우트의 폴더 목록이 남는다. key로 강제 재마운트.
  if (isDrive) {
    return (
      <div className="t-subnav" style={{ width }}>
        <DriveSidebar key="drive" onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── 내부문서: same folder-tree sidebar, routed to /exec/docs ───────────────
  if (isExecDocs) {
    return (
      <div className="t-subnav" style={{ width }}>
        <DriveSidebar key="exec-docs" onCollapse={() => onCollapse(true)} basePath="/exec/docs" />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Notice: folder-tree sidebar (카테고리 > 세부탭, 권한 관리) ──────────────
  if (isNotice) {
    return (
      <div className="t-subnav" style={{ width }}>
        <NoticeSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Mail: folder-tree sidebar (메일함 · 라벨 · 외부 메일) ──────────────────
  if (isMail) {
    return (
      <div className="t-subnav t-subnav-mail" style={{ width }}>
        <MailSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Approval: folder-tree sidebar (결재함 · 양식 · 관리) ───────────────────
  if (isApproval) {
    return (
      <div className="t-subnav" style={{ width }}>
        <ApprovalSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Todos: page-tree sidebar (boards) ─────────────────────────────────────
  if (isTodos) {
    return (
      <div className="t-subnav" style={{ width }}>
        <TodosSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Chat: Tim Box room list sidebar ──────────────────────────────────────
  if (isChat) {
    return (
      <div className="t-subnav" style={{ width, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <ChatSidebar onCollapse={() => onCollapse(true)} />
        <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
      </div>
    );
  }

  // ── Other modules: static SUBNAVS (with board-cat dynamic items) ─────────

  const activeFilter = params.get("filter") || subnav!.groups[0]?.items[0]?.id;

  const addCategory = async () => {
    const label = newCatLabel.trim();
    if (!label) { setAddingCat(false); return; }
    const code = label.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
    try {
      await api.post("/forum/categories", { code, label, color: "info" });
      toast.success("카테고리 추가됨");
      setNewCatLabel(""); setAddingCat(false);
      reloadCats();
      window.dispatchEvent(new Event("forum-categories-updated"));
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    }
  };

  const editCategory = async (code: string, newLabel: string) => {
    try {
      await api.patch(`/forum/categories/${code}`, { label: newLabel });
      reloadCats();
      window.dispatchEvent(new Event("forum-categories-updated"));
    } catch (e: any) {
      toast.error("수정 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const deleteCategory = async (code: string) => {
    const cat = cats.find(c => c.code === code);
    if (!window.confirm(`"${cat?.label ?? code}" 카테고리를 삭제하시겠습니까?\n해당 카테고리의 기존 게시글은 유지됩니다.`)) return;
    try {
      await api.delete(`/forum/categories/${code}`);
      toast.success("카테고리 삭제됨");
      reloadCats();
      window.dispatchEvent(new Event("forum-categories-updated"));
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const moveCategory = async (code: string, dir: "up" | "down") => {
    const sorted = [...cats].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(c => c.code === code);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
    const codes = sorted.map(c => c.code);
    try {
      const r = await api.put("/forum/categories/reorder", { codes });
      setCats(r.data);
      window.dispatchEvent(new Event("forum-categories-updated"));
    } catch {
      toast.error("순서 변경 실패");
    }
  };

  // ── Notice category handlers ─────────────────────────────────────────────
  const addNoticeCategory = async () => {
    const label = newNoticeCatLabel.trim();
    if (!label) { setAddingNoticeCat(false); return; }
    const code = label.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
    try {
      await api.post("/notice/categories", { code, label, color: "info" });
      toast.success("카테고리 추가됨");
      setNewNoticeCatLabel(""); setAddingNoticeCat(false);
      reloadNoticeCats();
      window.dispatchEvent(new Event("notice-categories-updated"));
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    }
  };

  const editNoticeCategory = async (code: string, newLabel: string) => {
    try {
      await api.patch(`/notice/categories/${code}`, { label: newLabel });
      reloadNoticeCats();
      window.dispatchEvent(new Event("notice-categories-updated"));
    } catch (e: any) {
      toast.error("수정 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const deleteNoticeCategory = async (code: string) => {
    const cat = noticeCats.find(c => c.code === code);
    if (!window.confirm(`"${cat?.label ?? code}" 카테고리를 삭제하시겠습니까?\n기존 공지는 유지됩니다.`)) return;
    try {
      await api.delete(`/notice/categories/${code}`);
      toast.success("카테고리 삭제됨");
      reloadNoticeCats();
      window.dispatchEvent(new Event("notice-categories-updated"));
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const moveNoticeCategory = async (code: string, dir: "up" | "down") => {
    const sorted = [...noticeCats].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(c => c.code === code);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
    const codes = sorted.map(c => c.code);
    try {
      const r = await api.put("/notice/categories/reorder", { codes });
      setNoticeCats(r.data);
      window.dispatchEvent(new Event("notice-categories-updated"));
    } catch {
      toast.error("순서 변경 실패");
    }
  };

  // ── Reference category handlers (팀장급 이상) ────────────────────────────
  const addRefCategory = async () => {
    const label = newRefCatLabel.trim();
    if (!label) { setAddingRefCat(false); return; }
    try {
      await api.post("/references/categories", { label });
      toast.success("카테고리 추가됨");
      setNewRefCatLabel(""); setAddingRefCat(false);
      reloadRefCats();
      window.dispatchEvent(new Event("reference-categories-updated"));
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    }
  };

  const editRefCategory = async (id: string, newLabel: string) => {
    try {
      await api.patch(`/references/categories/${id}`, { label: newLabel });
      reloadRefCats();
      window.dispatchEvent(new Event("reference-categories-updated"));
    } catch (e: any) {
      toast.error("수정 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const deleteRefCategory = async (id: string) => {
    const c = refCats.find(x => String(x.id) === id);
    if (!window.confirm(`"${c?.label ?? id}" 카테고리를 삭제하시겠습니까?\n폴더 안의 파일도 함께 삭제됩니다.`)) return;
    try {
      await api.delete(`/references/categories/${id}`);
      toast.success("카테고리 삭제됨");
      reloadRefCats();
      window.dispatchEvent(new Event("reference-categories-updated"));
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const moveRefCategory = async (id: string, dir: "up" | "down") => {
    const sorted = [...refCats].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(c => String(c.id) === id);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
    const ids = sorted.map(c => c.id);
    try {
      const r = await api.put("/references/categories/reorder", { ids });
      setRefCats(r.data);
      window.dispatchEvent(new Event("reference-categories-updated"));
    } catch {
      toast.error("순서 변경 실패");
    }
  };

  const selectItem = (itemId: string) => {
    const np = new URLSearchParams(params);
    np.set("filter", itemId);
    setParams(np, { replace: false });
    if (mod) nav(`${mod.path}?filter=${itemId}`);
  };

  return (
    <div className="t-subnav" style={{ width }}>
      <div className="t-subnav-header">
        <div>
          <h3>{subnav!.title}</h3>
          {subnav!.subtitle && <p>{subnav!.subtitle}</p>}
        </div>
        <button className="t-subnav-collapse" onClick={() => onCollapse(true)} title="접기">
          <I.PanelL size={14}/>
        </button>
      </div>

      <div className="t-subnav-body">
        {subnav!.groups.map((g) => {
          const isBoardCat  = isBoard  && g.id === "cat";
          const isNoticeCat = isNotice && g.id === "cat";
          const isRefCat    = isRef    && g.id === "cat";
          const isDynCat = isBoardCat || isNoticeCat || isRefCat;
          // Which role-tier may manage this group's categories.
          const groupCanManage = isRefCat ? canManageRef : isAdmin;

          const rawItems: { id: string; label: string; color?: string; menuKey?: string }[] =
            isBoardCat && cats.length > 0
              ? [{ id: "", label: "전체" }, ...cats.map(c => ({ id: c.code, label: c.label, color: (c as any).color }))]
              : isNoticeCat && noticeCats.length > 0
                ? [{ id: "", label: "전체" }, ...noticeCats.map(c => ({ id: c.code, label: c.label, color: c.color }))]
                : isRefCat
                  ? [{ id: "", label: "전체" }, ...refCats.map(c => ({ id: String(c.id), label: c.label }))]
                  : g.items;

          // Hide items gated by a menuKey the user can't access (e.g. admin tabs).
          const items = isDynCat
            ? rawItems
            : rawItems.filter(it => !it.menuKey || authManifest?.menus?.[it.menuKey] === true);

          // Skip a static group entirely if nothing is visible in it.
          if (!isDynCat && items.length === 0) return null;

          return (
            <div className="t-subnav-group" key={g.id}>
              <div className="t-subnav-group-title" style={{ display: "flex", alignItems: "center" }}>
                <span style={{ flex: 1 }}>{g.title}</span>
                {isDynCat && groupCanManage && (
                  <button
                    title="카테고리 추가"
                    onClick={() => {
                      if (isBoardCat) { setAddingCat(true); setTimeout(() => newCatInputRef.current?.focus(), 0); }
                      else if (isNoticeCat) { setAddingNoticeCat(true); setTimeout(() => newNoticeCatInputRef.current?.focus(), 0); }
                      else { setAddingRefCat(true); setTimeout(() => newRefCatInputRef.current?.focus(), 0); }
                    }}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "var(--text-3)", padding: "0 4px", fontSize: 14, lineHeight: 1,
                      borderRadius: 4, display: "flex", alignItems: "center",
                    }}
                    onMouseOver={e => (e.currentTarget.style.color = "var(--p-500, #6366f1)")}
                    onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}
                  >
                    <I.Plus size={12}/>
                  </button>
                )}
              </div>

              {items.map((item, idx) => (
                isDynCat ? (
                  <CatItem
                    key={item.id}
                    cat={item}
                    active={activeFilter === item.id}
                    isAdmin={groupCanManage}
                    onSelect={() => selectItem(item.id)}
                    onEdit={newLabel =>
                      isBoardCat ? editCategory(item.id, newLabel)
                        : isNoticeCat ? editNoticeCategory(item.id, newLabel)
                        : editRefCategory(item.id, newLabel)
                    }
                    onDelete={() =>
                      isBoardCat ? deleteCategory(item.id)
                        : isNoticeCat ? deleteNoticeCategory(item.id)
                        : deleteRefCategory(item.id)
                    }
                    onMove={dir =>
                      isBoardCat ? moveCategory(item.id, dir)
                        : isNoticeCat ? moveNoticeCategory(item.id, dir)
                        : moveRefCategory(item.id, dir)
                    }
                    isFirst={item.id === "" || idx === 1}
                    isLast={idx === items.length - 1}
                  />
                ) : (
                  <button
                    key={item.id}
                    className={`t-subnav-item ${activeFilter === item.id ? "active" : ""}`}
                    onClick={() => selectItem(item.id)}
                  >
                    <span>{item.label}</span>
                    {(item as any).badge && <span className="sm-badge">{(item as any).badge}</span>}
                  </button>
                )
              ))}

              {isBoardCat && addingCat && (
                <div style={{
                  display: "flex", gap: 4, margin: "2px 6px", padding: "5px 8px",
                  alignItems: "center", background: "rgba(99,102,241,0.07)",
                  borderRadius: 6, border: "1px dashed var(--p-400, #818cf8)",
                }}>
                  <span style={{ fontSize: 10, color: "var(--p-400)" }}>+</span>
                  <input
                    ref={newCatInputRef}
                    value={newCatLabel}
                    placeholder="카테고리 이름 입력…"
                    onChange={e => setNewCatLabel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") addCategory();
                      if (e.key === "Escape") { setAddingCat(false); setNewCatLabel(""); }
                    }}
                    style={{
                      flex: 1, fontSize: 12, padding: "1px 0",
                      border: "none", outline: "none",
                      background: "transparent", color: "var(--text)",
                    }}
                  />
                  <button onClick={addCategory}
                    style={{ ...catActionBtn, color: "var(--p-500, #6366f1)", fontWeight: 700, fontSize: 13 }}
                    title="추가">✓</button>
                  <button onClick={() => { setAddingCat(false); setNewCatLabel(""); }}
                    style={{ ...catActionBtn, fontSize: 13 }}
                    title="취소">✕</button>
                </div>
              )}

              {isNoticeCat && addingNoticeCat && (
                <div style={{
                  display: "flex", gap: 4, margin: "2px 6px", padding: "5px 8px",
                  alignItems: "center", background: "rgba(99,102,241,0.07)",
                  borderRadius: 6, border: "1px dashed var(--p-400, #818cf8)",
                }}>
                  <span style={{ fontSize: 10, color: "var(--p-400)" }}>+</span>
                  <input
                    ref={newNoticeCatInputRef}
                    value={newNoticeCatLabel}
                    placeholder="카테고리 이름 입력…"
                    onChange={e => setNewNoticeCatLabel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") addNoticeCategory();
                      if (e.key === "Escape") { setAddingNoticeCat(false); setNewNoticeCatLabel(""); }
                    }}
                    style={{
                      flex: 1, fontSize: 12, padding: "1px 0",
                      border: "none", outline: "none",
                      background: "transparent", color: "var(--text)",
                    }}
                  />
                  <button onClick={addNoticeCategory}
                    style={{ ...catActionBtn, color: "var(--p-500, #6366f1)", fontWeight: 700, fontSize: 13 }}
                    title="추가">✓</button>
                  <button onClick={() => { setAddingNoticeCat(false); setNewNoticeCatLabel(""); }}
                    style={{ ...catActionBtn, fontSize: 13 }}
                    title="취소">✕</button>
                </div>
              )}

              {isRefCat && addingRefCat && (
                <div style={{
                  display: "flex", gap: 4, margin: "2px 6px", padding: "5px 8px",
                  alignItems: "center", background: "rgba(99,102,241,0.07)",
                  borderRadius: 6, border: "1px dashed var(--p-400, #818cf8)",
                }}>
                  <span style={{ fontSize: 10, color: "var(--p-400)" }}>+</span>
                  <input
                    ref={newRefCatInputRef}
                    value={newRefCatLabel}
                    placeholder="카테고리 이름 입력…"
                    onChange={e => setNewRefCatLabel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") addRefCategory();
                      if (e.key === "Escape") { setAddingRefCat(false); setNewRefCatLabel(""); }
                    }}
                    style={{
                      flex: 1, fontSize: 12, padding: "1px 0",
                      border: "none", outline: "none",
                      background: "transparent", color: "var(--text)",
                    }}
                  />
                  <button onClick={addRefCategory}
                    style={{ ...catActionBtn, color: "var(--p-500, #6366f1)", fontWeight: 700, fontSize: 13 }}
                    title="추가">✓</button>
                  <button onClick={() => { setAddingRefCat(false); setNewRefCatLabel(""); }}
                    style={{ ...catActionBtn, fontSize: 13 }}
                    title="취소">✕</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="t-subnav-resize-handle" onMouseDown={startDrag}/>
    </div>
  );
}

// ── ChatSidebar — Tim Box room list (rendered inside SubNav on /chat) ─────────

type ChatRoom = { id: number; type: string; name: string | null; member_count: number; unread_count?: number; description?: string | null; folder_id?: number | null; position?: number | null; icon?: string | null };
type ChatFolderT = { id: number; name: string; position?: number };
type ChatMemberLite = { id: number; name: string; department_name?: string | null };

const CHAT_ROOM_ICON: Record<string, string> = {
  GROUP: "#", DEPARTMENT: "🏢", DIRECT: "💬", PROJECT: "📁", OPEN: "🌐",
};

// 프레즌스 점 색 — ChatDashboard 와 같은 기준 (접속중/1시간 이내/미접속)
function chatPresenceColor(lastSeen: number | undefined): string {
  if (lastSeen == null) return "var(--border)";
  const age = Date.now() - lastSeen;
  if (age <= 70_000) return "var(--ok, #16A34A)";
  if (age <= 60 * 60_000) return "#F59E0B";
  return "var(--border)";
}

function ChatSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: me } = useAuth();
  const [rooms, setRooms] = useState<ChatRoom[] | null>(null);
  const [archived, setArchived] = useState<ChatRoom[]>([]);
  const [folders, setFolders] = useState<ChatFolderT[]>([]);
  const [members, setMembers] = useState<ChatMemberLite[]>([]);
  const [presence, setPresence] = useState<Record<number, number>>({});
  const [search, setSearch] = useState("");
  const [collapseChannels, setCollapseChannels] = useState(false);
  const [collapseDMs, setCollapseDMs] = useState(false);
  const [collapseArchived, setCollapseArchived] = useState(true);   // 보관함은 접힌 채 시작
  const [collapsedFolders, setCollapsedFolders] = useState<Record<number, boolean>>({});
  const dmBusy = useRef(false);

  const activeRoomId = Number(searchParams.get("room")) || null;

  const loadRooms = () => {
    api.get<ChatRoom[]>("/chat/rooms").then((r) => setRooms(r.data)).catch(() => setRooms([]));
    api.get<ChatRoom[]>("/chat/rooms/archived").then((r) => setArchived(r.data)).catch(() => setArchived([]));
    api.get<ChatFolderT[]>("/chat/folders").then((r) => setFolders(r.data)).catch(() => setFolders([]));
  };

  useEffect(() => {
    loadRooms();
    window.addEventListener("chat-rooms-updated", loadRooms);
    const iv = window.setInterval(loadRooms, 30_000);   // 안읽음 배지 주기 갱신
    return () => { window.removeEventListener("chat-rooms-updated", loadRooms); window.clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 전체 멤버 + 프레즌스 — DM 섹션에서 아무나 골라 바로 1:1 을 열 수 있게
  useEffect(() => {
    let alive = true;
    api.get<any>("/users", { params: { size: 100, status: "ACTIVE" } })
      .then((r) => { if (!alive) return; const b: any = r.data; setMembers(Array.isArray(b) ? b : (b?.data ?? b?.items ?? [])); })
      .catch(() => {});
    const loadPresence = () =>
      api.get<{ user_id: number; last_seen: string }[]>("/chat/presence")
        .then((r) => { if (alive) setPresence(Object.fromEntries(r.data.map((p) => [p.user_id, new Date(p.last_seen).getTime()]))); })
        .catch(() => {});
    loadPresence();
    const iv = window.setInterval(loadPresence, 30_000);
    return () => { alive = false; window.clearInterval(iv); };
  }, []);

  const all   = rooms ?? [];
  const chans = all.filter((r) => r.type !== "DIRECT" && (!search || (r.name ?? "").toLowerCase().includes(search.toLowerCase())));
  const dms   = all.filter((r) => r.type === "DIRECT"  && (!search || (r.name ?? "").toLowerCase().includes(search.toLowerCase())));
  const dmMembers = members.filter((m) => m.id !== me?.id && (!search || m.name.toLowerCase().includes(search.toLowerCase())));

  const goRoom     = (id: number) => nav(`/chat?room=${id}`);
  const goCreate   = () => nav("/chat?modal=create");
  const goAiManage = () => nav(`/chat?modal=ai${activeRoomId ? `&room=${activeRoomId}` : ""}`);

  // 멤버 더블클릭/우클릭 → 1:1 DM 열기 (없으면 생성 — 서버가 중복 생성을 막는다)
  const openDmWith = async (m: ChatMemberLite) => {
    if (dmBusy.current) return;
    dmBusy.current = true;
    try {
      const r = await api.post<ChatRoom>(`/chat/dm/${m.id}`);
      window.dispatchEvent(new Event("chat-rooms-updated"));
      nav(`/chat?room=${r.data.id}`);
    } catch (e: any) {
      toast.error("DM 열기 실패", e?.response?.data?.error?.message ?? "");
    } finally { dmBusy.current = false; }
  };

  // ── 방 우클릭 메뉴: 수정·삭제 ──────────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; room: ChatRoom } | null>(null);
  // 목록의 빈 공간 우클릭 — 새 채팅방·새 폴더·새로고침
  const [blankCtx, setBlankCtx] = useState<{ x: number; y: number } | null>(null);
  const [editRoom, setEditRoom] = useState<ChatRoom | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editIcon, setEditIcon] = useState("");

  const startEdit = (r: ChatRoom) => {
    setCtxMenu(null);
    setEditRoom(r); setEditName(r.name ?? ""); setEditDesc(r.description ?? ""); setEditIcon(r.icon ?? "");
  };

  const saveEdit = async () => {
    if (!editRoom) return;
    try {
      await api.patch(`/chat/rooms/${editRoom.id}`, { name: editName.trim() || null, description: editDesc, icon: editIcon });
      toast.success("채팅방 수정됨");
      setEditRoom(null);
      loadRooms();
      window.dispatchEvent(new Event("chat-rooms-updated"));
    } catch (e: any) {
      toast.error("수정 실패", e?.response?.data?.error?.message ?? "방 관리자만 수정할 수 있습니다");
    }
  };

  const archiveRoom = async (r: ChatRoom, label: "보관" | "삭제") => {
    setCtxMenu(null);
    if (label === "삭제" && !window.confirm(`"${r.name || `방 ${r.id}`}" 채팅방을 삭제할까요?\n(보관함으로 이동하며 복원할 수 있습니다)`)) return;
    try {
      await api.delete(`/chat/rooms/${r.id}`);
      toast.success(label === "보관" ? "보관함으로 이동됨" : "채팅방 삭제됨(보관함에서 복원 가능)");
      loadRooms();
      window.dispatchEvent(new Event("chat-rooms-updated"));
      if (activeRoomId === r.id) nav("/chat");
    } catch (e: any) {
      toast.error(`${label} 실패`, e?.response?.data?.error?.message ?? "방 관리자만 가능합니다");
    }
  };

  const restoreRoom = async (r: ChatRoom) => {
    try {
      await api.post(`/chat/rooms/${r.id}/restore`);
      toast.success("복원됨", `"${r.name || `방 ${r.id}`}" 이(가) 목록으로 돌아왔습니다`);
      loadRooms();
      window.dispatchEvent(new Event("chat-rooms-updated"));
    } catch (e: any) {
      toast.error("복원 실패", e?.response?.data?.error?.message ?? "방 관리자만 복원할 수 있습니다");
    }
  };

  // ── 폴더: 만들기·이름변경·삭제·방 배정 ─────────────────────────────────────
  const [folderCtx, setFolderCtx] = useState<{ x: number; y: number; folder: ChatFolderT } | null>(null);
  const [editFolder, setEditFolder] = useState<ChatFolderT | null>(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [folderPickRoom, setFolderPickRoom] = useState<ChatRoom | null>(null); // 폴더 지정 모달 대상
  const [newFolderName, setNewFolderName] = useState("");

  const createFolder = async (name: string): Promise<ChatFolderT | null> => {
    const n = name.trim();
    if (!n) return null;
    try {
      const r = await api.post<ChatFolderT>("/chat/folders", { name: n });
      setNewFolderName("");
      loadRooms();
      return r.data;
    } catch (e: any) {
      toast.error("폴더 생성 실패", e?.response?.data?.error?.message ?? "");
      return null;
    }
  };

  const assignFolder = async (room: ChatRoom, folderId: number | null) => {
    try {
      await api.put(`/chat/rooms/${room.id}/folder`, { folder_id: folderId });
      setFolderPickRoom(null);
      loadRooms();
    } catch (e: any) {
      toast.error("폴더 지정 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const saveFolderName = async () => {
    if (!editFolder) return;
    try {
      await api.patch(`/chat/folders/${editFolder.id}`, { name: editFolderName });
      setEditFolder(null);
      loadRooms();
    } catch (e: any) {
      toast.error("이름 변경 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  // ── 드래그 정렬: 폴더·채널이 한 축으로 섞여 이동, 위치 표시선 지원 ─────────────
  const dragItem = useRef<{ kind: "room" | "folder"; id: number } | null>(null);
  // 드롭 위치 미리보기 — key = "f-{id}" | "r-{id}", where: before(위 삽입선) | into(폴더 안 강조)
  const [dropHint, setDropHint] = useState<{ key: string; where: "before" | "into" } | null>(null);

  const clearDrag = () => { dragItem.current = null; setDropHint(null); };

  // 행 위에서 드래그 중 — 폴더 헤더는 상단 45% 는 '앞으로', 아래는 '폴더 안으로'
  const overRow = (e: React.DragEvent, key: string, kind: "folder" | "room") => {
    if (!dragItem.current) return;
    e.preventDefault();
    let where: "before" | "into" = "before";
    if (kind === "folder" && dragItem.current.kind === "room") {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      where = (e.clientY - rect.top) < rect.height * 0.45 ? "before" : "into";
    }
    setDropHint((prev) => (prev?.key === key && prev.where === where) ? prev : { key, where });
  };

  // 놓기 — 현재 화면 배치를 재구성해 통째로 저장 (폴더가 최상단이 아니어도 됨)
  const performDrop = async (target: { kind: "folder" | "room"; id: number }, where: "before" | "into") => {
    const d = dragItem.current;
    clearDrag();
    if (!d) return;
    const chansAll = (rooms ?? []).filter((r) => r.type !== "DIRECT");
    const validFolder = (fid: number | null | undefined) => (fid != null && folders.some((f) => f.id === fid)) ? fid : null;
    const roomFolder = new Map<number, number | null>(chansAll.map((r) => [r.id, validFolder(r.folder_id)]));
    const folderRooms = new Map<number, number[]>(folders.map((f) => [f.id, chansAll.filter((r) => validFolder(r.folder_id) === f.id).map((r) => r.id)]));
    const BIG = 1e15;
    let top: { kind: "folder" | "room"; id: number }[] = [
      ...folders.map((f) => ({ kind: "folder" as const, id: f.id, pos: f.position ?? BIG })),
      ...chansAll.filter((r) => validFolder(r.folder_id) == null).map((r) => ({ kind: "room" as const, id: r.id, pos: r.position ?? BIG })),
    ].sort((a, b) => a.pos - b.pos).map(({ kind, id }) => ({ kind, id }));

    const removeRoom = (rid: number) => {
      top = top.filter((t) => !(t.kind === "room" && t.id === rid));
      folderRooms.forEach((arr, fid) => folderRooms.set(fid, arr.filter((x) => x !== rid)));
    };

    let folderChange: number | null | undefined;   // undefined = 배정 변경 없음

    if (d.kind === "room") {
      const rid = d.id;
      if (target.kind === "room" && target.id === rid) return;
      const prevFolder = roomFolder.get(rid) ?? null;
      if (target.kind === "folder" && where === "into") {
        removeRoom(rid);
        folderRooms.get(target.id)?.unshift(rid);
        folderChange = target.id;
      } else if (target.kind === "folder") {           // 폴더 '앞'에 → 미분류로 그 위치에
        removeRoom(rid);
        const idx = top.findIndex((t) => t.kind === "folder" && t.id === target.id);
        top.splice(Math.max(idx, 0), 0, { kind: "room", id: rid });
        folderChange = null;
      } else {                                          // 다른 방 앞에
        const tFolder = roomFolder.get(target.id) ?? null;
        removeRoom(rid);
        if (tFolder == null) {
          const idx = top.findIndex((t) => t.kind === "room" && t.id === target.id);
          top.splice(Math.max(idx, 0), 0, { kind: "room", id: rid });
        } else {
          const arr = folderRooms.get(tFolder)!;
          arr.splice(Math.max(arr.indexOf(target.id), 0), 0, rid);
        }
        folderChange = tFolder;
      }
      if (folderChange === prevFolder) folderChange = undefined;
    } else {
      const fid = d.id;
      const beforeFolder = target.kind === "folder" ? target.id : (roomFolder.get(target.id) ?? null);
      if (beforeFolder === fid) return;                 // 자기 자신/자기 방 위 — 무시
      top = top.filter((t) => !(t.kind === "folder" && t.id === fid));
      let idx = beforeFolder != null
        ? top.findIndex((t) => t.kind === "folder" && t.id === beforeFolder)
        : top.findIndex((t) => t.kind === "room" && t.id === target.id);
      if (idx < 0) idx = top.length;
      top.splice(idx, 0, { kind: "folder", id: fid });
    }

    // 화면 순서 그대로 평탄화(폴더 뒤에 그 폴더의 방들) → 통째로 저장
    const items: { kind: string; id: number }[] = [];
    for (const t of top) {
      items.push(t);
      if (t.kind === "folder") for (const rid of folderRooms.get(t.id) ?? []) items.push({ kind: "room", id: rid });
    }
    try {
      if (d.kind === "room" && folderChange !== undefined) {
        await api.put(`/chat/rooms/${d.id}/folder`, { folder_id: folderChange });
      }
      await api.put("/chat/sidebar/reorder", { items });
      loadRooms();
    } catch (e: any) {
      toast.error("이동 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const removeFolder = async (f: ChatFolderT) => {
    setFolderCtx(null);
    if (!window.confirm(`"${f.name}" 폴더를 삭제할까요?\n(안의 채팅방은 그대로 남고 폴더에서만 나옵니다)`)) return;
    try {
      await api.delete(`/chat/folders/${f.id}`);
      toast.success("폴더 삭제됨");
      loadRooms();
    } catch (e: any) {
      toast.error("폴더 삭제 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const roomBtn = (r: ChatRoom, indent = false) => {
    const isActive = activeRoomId === r.id;
    const draggable = r.type !== "DIRECT";
    const hintKey = `r-${r.id}`;
    const hinted = dropHint?.key === hintKey;
    return (
      <button key={r.id} onClick={() => goRoom(r.id)}
        onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, room: r }); }}
        draggable={draggable}
        onDragStart={() => { if (draggable) dragItem.current = { kind: "room", id: r.id }; }}
        onDragEnd={clearDrag}
        onDragOver={(e) => { if (draggable) overRow(e, hintKey, "room"); }}
        onDrop={(e) => { if (draggable) { e.preventDefault(); performDrop({ kind: "room", id: r.id }, "before"); } }}
        title="우클릭: 수정·폴더·보관·삭제 / 드래그: 순서·폴더 이동"
        style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: `5px 12px 5px ${indent ? 30 : 16}px`, width: "100%", border: "none",
          cursor: "pointer", textAlign: "left", borderRadius: 6,
          boxShadow: hinted ? "0 -2px 0 0 var(--p-500,#6366f1)" : undefined,   // 삽입 위치 표시선
          background: isActive ? "rgba(99,102,241,0.18)" : "transparent",
          color: isActive ? "var(--p-400)" : "var(--text-2)",
          fontWeight: isActive ? 700 : 400, transition: "background 0.1s",
        }}
        onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.07)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = isActive ? "rgba(99,102,241,0.18)" : "transparent"; }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, flexShrink: 0, width: 16, textAlign: "center", color: isActive ? "var(--p-400)" : "var(--text-3)" }}>
          {r.icon || CHAT_ROOM_ICON[r.type] || "#"}
        </span>
        <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: (r.unread_count ?? 0) > 0 ? 700 : undefined }}>
          {r.name || `방 ${r.id}`}
        </span>
        {(r.unread_count ?? 0) > 0 ? (
          <span title={`안읽은 메시지 ${r.unread_count}개`} style={{
            fontSize: 10, fontWeight: 800, color: "#fff", background: "var(--p-500,#6366f1)",
            borderRadius: 999, minWidth: 17, height: 17, lineHeight: "17px", textAlign: "center",
            padding: "0 5px", flexShrink: 0, boxSizing: "border-box",
          }}>{r.unread_count! > 99 ? "99+" : r.unread_count}</span>
        ) : (
          <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>{r.member_count}</span>
        )}
      </button>
    );
  };

  // 폴더 헤더 행 — 접기/펼치기 + 우클릭(이름 변경·삭제) + 드롭 대상(앞/안 구분)
  const folderHeader = (f: ChatFolderT, count: number) => {
    const closed = !!collapsedFolders[f.id];
    const hintKey = `f-${f.id}`;
    const hint = dropHint?.key === hintKey ? dropHint.where : null;
    return (
      <button key={`fh-${f.id}`}
        onClick={() => setCollapsedFolders((p) => ({ ...p, [f.id]: !p[f.id] }))}
        onContextMenu={(e) => { e.preventDefault(); setFolderCtx({ x: e.clientX, y: e.clientY, folder: f }); }}
        draggable
        onDragStart={() => { dragItem.current = { kind: "folder", id: f.id }; }}
        onDragEnd={clearDrag}
        onDragOver={(e) => overRow(e, hintKey, "folder")}
        onDrop={(e) => { e.preventDefault(); performDrop({ kind: "folder", id: f.id }, hint ?? "into"); }}
        title="우클릭: 이름 변경·삭제 / 드래그: 순서 이동 · 방 끌어넣기(위쪽=앞으로, 아래쪽=폴더 안으로)"
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "4px 12px 4px 14px", width: "100%", border: "none",
          background: hint === "into" ? "rgba(99,102,241,0.16)" : "none",       // 폴더 안으로 들어감 표시
          boxShadow: hint === "before" ? "0 -2px 0 0 var(--p-500,#6366f1)" : undefined,   // 앞 삽입 표시선
          cursor: "pointer", color: "var(--text-2)", borderRadius: 6,
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.06)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
        <span style={{ fontSize: 8, width: 10, color: "var(--text-3)" }}>{closed ? "▶" : "▼"}</span>
        <span style={{ fontSize: 13 }}>🗂</span>
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{f.name}</span>
        <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>{count}</span>
      </button>
    );
  };

  const sectionBtn = (label: string, collapsed: boolean, toggle: () => void) => (
    <button onClick={toggle}
      style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px 2px", width: "100%", border: "none", background: "none", cursor: "pointer", color: "var(--text-3)" }}>
      <span style={{ fontSize: 9 }}>{collapsed ? "▶" : "▼"}</span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{label}</span>
    </button>
  );

  return (
    <>
      {/* Workspace header */}
      <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <button
            onClick={() => nav("/chat")}
            title="채팅 대시보드"
            style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, border: "none", background: "transparent", cursor: "pointer", padding: "2px 4px", margin: "-2px -4px", borderRadius: 7 }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            <div style={{ width: 28, height: 28, borderRadius: 7, background: "var(--p-600)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 900, color: "#fff", flexShrink: 0 }}>T</div>
            <span style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)" }}>Tim Box</span>
          </button>
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            <button className="t-btn" style={{ fontSize: 12, padding: "2px 7px" }} onClick={onCollapse} title="접기">‹</button>
            <button className="t-btn" style={{ fontSize: 18, padding: "0 4px", lineHeight: 1 }} title="새 채팅방" onClick={goCreate}>+</button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: "8px 10px 4px", flexShrink: 0 }}>
        <input className="t-input" style={{ fontSize: 12, padding: "5px 10px", width: "100%", boxSizing: "border-box" as const }}
          placeholder="🔍  채팅방 검색…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Room list — 빈 공간 우클릭: 새 채팅방·폴더·새로고침 */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px" }}
        onContextMenu={(e) => {
          if (e.defaultPrevented) return;   // 방·폴더·멤버 행이 이미 자기 메뉴를 띄운 경우
          e.preventDefault();
          setBlankCtx({ x: e.clientX, y: e.clientY });
        }}>
        {rooms === null && <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)", fontSize: 12 }}>불러오는 중…</div>}

        {/* 채널 — 폴더 그룹 + 미분류 */}
        <div style={{ marginBottom: 4 }}>
          {sectionBtn("채널", collapseChannels, () => setCollapseChannels((v) => !v))}
          {!collapseChannels && (
            <>
              {/* 폴더·미분류 채널을 한 축으로 섞어 정렬 (position 순 — 폴더가 최상단이 아니어도 됨) */}
              {(() => {
                const BIG = 1e15;
                const ungrouped = chans.filter((r) => !r.folder_id || !folders.some((f) => f.id === r.folder_id));
                const top = [
                  ...folders.map((f) => ({ kind: "folder" as const, f, pos: f.position ?? BIG })),
                  ...ungrouped.map((r) => ({ kind: "room" as const, r, pos: r.position ?? BIG })),
                ].sort((a, b) => a.pos - b.pos);
                return top.map((t) => {
                  if (t.kind === "room") return roomBtn(t.r);
                  const inF = chans.filter((r) => r.folder_id === t.f.id);
                  if (search && inF.length === 0) return null;   // 검색 중엔 빈 폴더 숨김
                  return (
                    <div key={`f-${t.f.id}`}>
                      {folderHeader(t.f, inF.length)}
                      {!collapsedFolders[t.f.id] && inF.map((r) => roomBtn(r, true))}
                    </div>
                  );
                });
              })()}
              <div style={{ display: "flex" }}>
                <button onClick={goCreate}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px 4px 16px", border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 12 }}>
                  <span>＋</span><span>채널 추가</span>
                </button>
                <button onClick={async () => { const n = window.prompt("새 폴더 이름"); if (n) await createFolder(n); }}
                  title="채팅방을 묶을 폴더 만들기 (방 우클릭 → 폴더 지정)"
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 12 }}>
                  <span>🗂＋</span><span>폴더</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* DM — 기존 1:1 방(클릭 이동) + 전체 멤버(더블클릭·우클릭으로 1:1 열기) */}
        {(dms.length > 0 || dmMembers.length > 0 || !search) && (
          <div>
            {sectionBtn("다이렉트 메시지", collapseDMs, () => setCollapseDMs((v) => !v))}
            {!collapseDMs && (
              <>
                {dms.map((r) => roomBtn(r))}
                {dmMembers.length > 0 && (
                  <div style={{ padding: "6px 8px 2px 16px", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-3)", textTransform: "uppercase" as const }}>
                    전체 멤버
                  </div>
                )}
                {dmMembers.map((m) => (
                  <button key={`m-${m.id}`}
                    onDoubleClick={() => openDmWith(m)}
                    onContextMenu={(e) => { e.preventDefault(); openDmWith(m); }}
                    title="더블클릭 또는 우클릭: 1:1 채팅 열기"
                    style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "4px 12px 4px 16px", width: "100%", border: "none",
                      cursor: "pointer", textAlign: "left", borderRadius: 6,
                      background: "transparent", color: "var(--text-2)", transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.07)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  >
                    <span style={{
                      width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                      background: "color-mix(in srgb, var(--p-500) 22%, transparent)", color: "var(--p-400)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 800,
                    }}>{m.name.slice(0, 2)}</span>
                    <span style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.name}
                    </span>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: chatPresenceColor(presence[m.id]) }} />
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {/* 보관된 채팅방 — 지난 채팅 열람·복원 (폴더 그룹 유지) */}
        {archived.length > 0 && (() => {
          const archFiltered = archived.filter((r) => !search || (r.name ?? "").toLowerCase().includes(search.toLowerCase()));
          const archRow = (r: ChatRoom, indent = false) => (
            <div key={`a-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: `4px 12px 4px ${indent ? 30 : 16}px`, borderRadius: 6, color: "var(--text-3)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "rgba(99,102,241,0.07)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>📦</span>
              <button onClick={() => goRoom(r.id)} title="지난 대화 보기"
                onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, room: r }); }}
                style={{ flex: 1, minWidth: 0, border: "none", background: "none", cursor: "pointer", textAlign: "left", fontSize: 12.5, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: 0 }}>
                {r.name || `방 ${r.id}`}
              </button>
              <button onClick={() => restoreRoom(r)} title="목록으로 복원"
                style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12, color: "var(--p-500)", fontWeight: 700, flexShrink: 0, padding: "1px 4px" }}>
                ♻️ 복원
              </button>
            </div>
          );
          return (
            <div style={{ marginTop: 4 }}>
              {sectionBtn(`보관됨 (${archived.length})`, collapseArchived, () => setCollapseArchived((v) => !v))}
              {!collapseArchived && (
                <>
                  {folders.map((f) => {
                    const inF = archFiltered.filter((r) => r.folder_id === f.id);
                    if (!inF.length) return null;
                    return (
                      <div key={`af-${f.id}`}>
                        {folderHeader(f, inF.length)}
                        {!collapsedFolders[f.id] && inF.map((r) => archRow(r, true))}
                      </div>
                    );
                  })}
                  {archFiltered.filter((r) => !r.folder_id || !folders.some((f) => f.id === r.folder_id)).map((r) => archRow(r))}
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* 방 우클릭 메뉴 */}
      {ctxMenu && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 998 }}
            onClick={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
          <div style={{
            position: "fixed", left: Math.min(ctxMenu.x, window.innerWidth - 170), top: Math.min(ctxMenu.y, window.innerHeight - 100), zIndex: 999,
            background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.2)", overflow: "hidden", minWidth: 150,
          }}>
            {ctxMenu.room.type !== "DIRECT" && (
              <button onClick={() => startEdit(ctxMenu.room)}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "var(--text)", textAlign: "left" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
                ✏️ 이름·설명 수정
              </button>
            )}
            <button onClick={() => { setFolderPickRoom(ctxMenu.room); setCtxMenu(null); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "var(--text)", textAlign: "left" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
              📁 폴더 지정…
            </button>
            <button onClick={() => archiveRoom(ctxMenu.room, "보관")}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "var(--text)", textAlign: "left" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
              📦 보관하기
            </button>
            <button onClick={() => archiveRoom(ctxMenu.room, "삭제")}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "#ef4444", textAlign: "left" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(239,68,68,0.08)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
              🗑 삭제
            </button>
          </div>
        </>
      )}

      {/* 방 이름·설명 수정 모달 */}
      {editRoom && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setEditRoom(null)}>
          <div style={{ background: "var(--bg-0)", borderRadius: 14, padding: 20, width: 420, maxHeight: "84vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 800 }}>채팅방 수정</h3>
            <label className="t-label">아이콘</label>
            <div style={{ marginBottom: 12 }}>
              <RoomIconPicker value={editIcon} onChange={setEditIcon} onClear={() => setEditIcon("")} />
            </div>
            <label className="t-label">이름</label>
            <input className="t-input" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditRoom(null); }}
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 10 }} />
            <label className="t-label">설명</label>
            <input className="t-input" value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditRoom(null); }}
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 16 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="t-btn" onClick={() => setEditRoom(null)}>취소</button>
              <button className="t-btn t-btn-primary" onClick={saveEdit} disabled={!editName.trim()}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 빈 공간 우클릭 메뉴 */}
      {blankCtx && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 998 }}
            onClick={() => setBlankCtx(null)} onContextMenu={(e) => { e.preventDefault(); setBlankCtx(null); }} />
          <div style={{
            position: "fixed", left: Math.min(blankCtx.x, window.innerWidth - 180), top: Math.min(blankCtx.y, window.innerHeight - 130), zIndex: 999,
            background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.2)", overflow: "hidden", minWidth: 160,
          }}>
            {[
              { label: "＋ 새 채팅방", run: () => goCreate() },
              { label: "🗂 새 폴더", run: async () => { const n = window.prompt("새 폴더 이름"); if (n) await createFolder(n); } },
              { label: "🔄 목록 새로고침", run: () => { loadRooms(); toast.success("목록 갱신됨"); } },
            ].map((it) => (
              <button key={it.label} onClick={() => { setBlankCtx(null); it.run(); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "var(--text)", textAlign: "left" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* 폴더 우클릭 메뉴 */}
      {folderCtx && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 998 }}
            onClick={() => setFolderCtx(null)} onContextMenu={(e) => { e.preventDefault(); setFolderCtx(null); }} />
          <div style={{
            position: "fixed", left: Math.min(folderCtx.x, window.innerWidth - 170), top: Math.min(folderCtx.y, window.innerHeight - 100), zIndex: 999,
            background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.2)", overflow: "hidden", minWidth: 150,
          }}>
            <button onClick={() => { setEditFolder(folderCtx.folder); setEditFolderName(folderCtx.folder.name); setFolderCtx(null); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "var(--text)", textAlign: "left" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
              ✏️ 이름 변경
            </button>
            <button onClick={() => removeFolder(folderCtx.folder)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "#ef4444", textAlign: "left" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(239,68,68,0.08)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
              🗑 폴더 삭제
            </button>
          </div>
        </>
      )}

      {/* 폴더 이름 변경 모달 */}
      {editFolder && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setEditFolder(null)}>
          <div style={{ background: "var(--bg-0)", borderRadius: 14, padding: 20, width: 300, boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 800 }}>📁 폴더 이름 변경</h3>
            <input className="t-input" value={editFolderName} onChange={(e) => setEditFolderName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") saveFolderName(); if (e.key === "Escape") setEditFolder(null); }}
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 14 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="t-btn" onClick={() => setEditFolder(null)}>취소</button>
              <button className="t-btn t-btn-primary" onClick={saveFolderName} disabled={!editFolderName.trim()}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 폴더 지정 모달 — 방을 폴더에 넣기/빼기, 새 폴더 즉석 생성 */}
      {folderPickRoom && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setFolderPickRoom(null)}>
          <div style={{ background: "var(--bg-0)", borderRadius: 14, padding: 20, width: 320, boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 800 }}>📁 폴더 지정</h3>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {folderPickRoom.name || `방 ${folderPickRoom.id}`}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 230, overflowY: "auto", marginBottom: 12 }}>
              {folders.map((f) => (
                <button key={f.id} onClick={() => assignFolder(folderPickRoom, f.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8,
                    cursor: "pointer", fontSize: 13, textAlign: "left",
                    background: folderPickRoom.folder_id === f.id ? "rgba(99,102,241,0.12)" : "var(--bg-0)",
                    color: "var(--text)", fontWeight: folderPickRoom.folder_id === f.id ? 700 : 400,
                  }}>
                  📁 <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  {folderPickRoom.folder_id === f.id && <span style={{ color: "var(--p-500)" }}>✓</span>}
                </button>
              ))}
              {folders.length === 0 && <div style={{ fontSize: 12, color: "var(--text-3)", padding: "8px 2px" }}>아직 폴더가 없습니다 — 아래에서 만들어 보세요.</div>}
              {folderPickRoom.folder_id != null && (
                <button onClick={() => assignFolder(folderPickRoom, null)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px dashed var(--border)", borderRadius: 8, cursor: "pointer", fontSize: 13, background: "var(--bg-0)", color: "var(--text-3)", textAlign: "left" }}>
                  ⛔ 폴더에서 꺼내기
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="t-input" placeholder="새 폴더 이름" value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={async (e) => { if (e.key === "Enter") { const f = await createFolder(newFolderName); if (f) assignFolder(folderPickRoom, f.id); } }}
                style={{ flex: 1, boxSizing: "border-box" }} />
              <button className="t-btn t-btn-primary" disabled={!newFolderName.trim()}
                onClick={async () => { const f = await createFolder(newFolderName); if (f) assignFolder(folderPickRoom, f.id); }}>
                만들고 넣기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI 봇 관리 */}
      <div style={{ borderTop: "1px solid var(--border)", padding: "6px 8px", flexShrink: 0 }}>
        <button onClick={goAiManage}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 8px 8px 16px", width: "100%", border: "none", cursor: "pointer", borderRadius: 8, background: "transparent", color: "var(--text-2)", transition: "background 0.15s" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.09)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
        >
          <span style={{ fontSize: 18 }}>🤖</span>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>AI 봇 관리</div>
            <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 1 }}>페르소나 추가 · 설정</div>
          </div>
          <span style={{ fontSize: 12, color: "var(--text-3)" }}>›</span>
        </button>
      </div>
    </>
  );
}
